import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button, Modal, Input, Select, Field, Spinner } from "./ui";
import { ROLE_OPTIONS } from "../lib/schemas";
import { cn } from "../lib/utils";
import { Plus, Pencil, Trash2, Search, Building2, Clock, AlertCircle, History, X, Settings2 } from "lucide-react";

const lstr = (o, lang) => (o ? (o[lang] ?? o.fr ?? "") : "");
const INT_KINDS = ["call", "email", "meeting", "note", "other"];

function EntityChip({ name, color, roles, lang }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border"
      style={{ borderColor: (color || "#64748B") + "55", color: color || "var(--text-secondary)" }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color || "#64748B" }} />
      {name}
      {roles?.length ? <span className="opacity-70">· {roles.map((r) => lstr(ROLE_OPTIONS.find((o) => o.value === r)?.label, lang)).join(", ")}</span> : null}
    </span>
  );
}

export default function ContactsRolodex() {
  const { t, lang, entities } = useApp();
  const loc = lang === "fr" ? "fr-FR" : "en-US";
  const [data, setData] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [entityFilter, setEntityFilter] = useState("");
  const [subTab, setSubTab] = useState("all");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [logTarget, setLogTarget] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const entMap = useMemo(() => Object.fromEntries(entities.map((e) => [e.id, e])), [entities]);
  const companyName = useCallback((id) => companies.find((c) => c.id === id)?.name || "—", [companies]);

  const load = useCallback(async () => {
    const url = entityFilter ? `/crm/contacts?entity_id=${entityFilter}` : "/crm/contacts";
    const { data } = await api.get(url);
    setData(data);
  }, [entityFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/data/companies").then(({ data }) => setCompanies(data)).catch(() => {}); }, []);

  const contacts = data?.contacts || [];
  const threshold = data?.threshold_days ?? 30;
  const needsCount = contacts.filter((c) => c.needs_update).length;

  const filtered = contacts.filter((c) => {
    if (subTab === "needs" && !c.needs_update) return false;
    if (!query) return true;
    const hay = `${c.name} ${c.title || ""} ${c.email || ""} ${companyName(c.company_id)}`.toLowerCase();
    return hay.includes(query.toLowerCase());
  });

  const save = async (form) => {
    if (form.id) await api.put(`/crm/contacts/${form.id}`, form);
    else await api.post("/crm/contacts", form);
    setEditing(null);
    load();
  };
  const remove = async (c) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/crm/contacts/${c.id}`);
    load();
  };

  const fmtDays = (d) => (d == null ? t("crm_never_contacted") : `${d}${t("crm_days_short")}`);

  if (!data) return <div className="py-16 flex justify-center"><Spinner className="w-6 h-6 text-text-secondary" /></div>;

  return (
    <div data-testid="contacts-rolodex">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-text-secondary">{t("crm_filter_entity")}</span>
          <Select value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} className="max-w-[200px]" data-testid="crm-entity-filter">
            <option value="">{t("crm_all_entities")}</option>
            {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </Select>
          <div className="inline-flex items-center gap-1 p-0.5 rounded-lg border bg-surface ml-1">
            <button onClick={() => setSubTab("all")} data-testid="crm-subtab-all"
              className={cn("px-3 py-1 rounded-md text-xs font-medium", subTab === "all" ? "bg-text-primary text-background" : "text-text-secondary")}>{t("crm_tab_all")}</button>
            <button onClick={() => setSubTab("needs")} data-testid="crm-subtab-needs"
              className={cn("inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium", subTab === "needs" ? "bg-amber-500 text-white" : "text-text-secondary")}>
              <AlertCircle className="w-3.5 h-3.5" />{t("crm_tab_needs")}
              {needsCount > 0 && <span className="px-1.5 rounded-full bg-amber-500/20 text-amber-600 text-[10px] font-bold tabular-nums">{needsCount}</span>}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("search")} data-testid="crm-search-people"
              className="rounded-lg border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-[var(--border-strong)]" />
          </div>
          <Button size="sm" variant="outline" onClick={() => setShowSettings(true)} data-testid="crm-settings-btn"><Settings2 className="w-3.5 h-3.5" /></Button>
          <Button size="sm" onClick={() => setEditing({})} data-testid="crm-add-contact"><Plus className="w-3.5 h-3.5" /> {t("add")}</Button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-12 text-center text-sm text-text-secondary" data-testid="crm-people-empty">
          {subTab === "needs" ? t("crm_needs_none") : t("crm_no_people")}
        </div>
      ) : (
        <div className="rounded-2xl border overflow-hidden" data-testid="crm-people-table">
          <table className="w-full text-sm">
            <thead className="bg-surface text-text-secondary text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left font-semibold px-5 py-2.5">{t("crm_name")}</th>
                <th className="text-left font-semibold px-5 py-2.5">{t("crm_company")}</th>
                <th className="text-left font-semibold px-5 py-2.5">{t("crm_col_entities")}</th>
                <th className="text-left font-semibold px-5 py-2.5">{t("crm_last_contact")}</th>
                <th className="px-5 py-2.5 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-t hover:bg-surface-hover transition-colors" data-testid={`contact-row-${c.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-medium">{c.name}</div>
                    {c.title && <div className="text-xs text-text-secondary">{c.title}</div>}
                  </td>
                  <td className="px-5 py-3 text-text-secondary">{c.company_id ? companyName(c.company_id) : "—"}</td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(c.entity_links || []).map((l) => {
                        const e = entMap[l.entity_id]; if (!e) return null;
                        return <EntityChip key={l.entity_id} name={e.name} color={e.color} roles={l.roles} lang={lang} />;
                      })}
                      {!(c.entity_links || []).length && <span className="text-xs text-text-secondary">—</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className={cn("inline-flex items-center gap-1.5 text-xs", c.needs_update ? "text-amber-600 font-medium" : "text-text-secondary")} data-testid={`contact-lastcontact-${c.id}`}>
                      <Clock className="w-3.5 h-3.5" />{fmtDays(c.days_since_contact)}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => setLogTarget(c)} data-testid={`contact-log-${c.id}`} className="!px-2 !py-1 text-xs">{t("crm_mark_followed")}</Button>
                      <button onClick={() => setEditing(c)} data-testid={`contact-edit-${c.id}`} className="p-1.5 rounded-md text-text-secondary hover:text-text-primary hover:bg-background"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(c)} data-testid={`contact-delete-${c.id}`} className="p-1.5 rounded-md text-text-secondary hover:text-red-400 hover:bg-background"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <ContactEditor contact={editing} companies={companies} entities={entities} onClose={() => setEditing(null)} onSave={save} />}
      {logTarget && <LogModal contact={logTarget} onClose={() => setLogTarget(null)} onDone={() => { setLogTarget(null); load(); }} />}
      {showSettings && <SettingsModal current={threshold} onClose={() => setShowSettings(false)} onSaved={() => { setShowSettings(false); load(); }} />}
    </div>
  );
}

function ContactEditor({ contact, companies, entities, onClose, onSave }) {
  const { t, lang } = useApp();
  const loc = lang === "fr" ? "fr-FR" : "en-US";
  const [f, setF] = useState({
    name: contact.name || "", title: contact.title || "", company_id: contact.company_id || "",
    email: contact.email || "", phone: contact.phone || "", notes: contact.notes || "",
    entity_links: (contact.entity_links || []).map((l) => ({ entity_id: l.entity_id, roles: l.roles || [] })),
  });
  const [timeline, setTimeline] = useState(contact.id ? null : []);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (contact.id) api.get(`/crm/contacts/${contact.id}/timeline`).then(({ data }) => setTimeline(data)).catch(() => setTimeline([]));
  }, [contact.id]);

  const usedEntities = f.entity_links.map((l) => l.entity_id);
  const addLink = () => { const avail = entities.find((e) => !usedEntities.includes(e.id)); if (avail) set("entity_links", [...f.entity_links, { entity_id: avail.id, roles: [] }]); };
  const updLink = (i, patch) => set("entity_links", f.entity_links.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const rmLink = (i) => set("entity_links", f.entity_links.filter((_, idx) => idx !== i));
  const toggleRole = (i, role) => { const l = f.entity_links[i]; const has = l.roles.includes(role); updLink(i, { roles: has ? l.roles.filter((r) => r !== role) : [...l.roles, role] }); };

  const submit = () => { if (!f.name.trim()) return; onSave({ ...f, id: contact.id, entity_links: f.entity_links.filter((l) => l.entity_id) }); };
  const kindLabel = (k) => t(`crm_int_${k}`);

  return (
    <Modal open onClose={onClose} title={contact.id ? t("crm_contact_edit") : t("crm_contact_new")}
      footer={<><Button variant="outline" onClick={onClose}>{t("cancel")}</Button><Button onClick={submit} disabled={!f.name.trim()} data-testid="contact-save">{t("save")}</Button></>}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("crm_name") + " *"}><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name" autoFocus /></Field>
          <Field label={t("crm_job_title")}><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="contact-title" /></Field>
          <Field label={t("crm_company")}>
            <Select value={f.company_id} onChange={(e) => set("company_id", e.target.value)} data-testid="contact-company">
              <option value="">{t("none")}</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label={t("crm_email")}><Input value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" /></Field>
          <Field label={t("crm_phone")}><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone" /></Field>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-text-secondary">{t("crm_contact_entities")}</span>
            <button type="button" onClick={addLink} disabled={usedEntities.length >= entities.length} data-testid="contact-add-entity"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-40"><Plus className="w-3.5 h-3.5" />{t("crm_add_entity_link")}</button>
          </div>
          <div className="space-y-2" data-testid="contact-entity-links">
            {f.entity_links.map((l, i) => (
              <div key={i} className="rounded-lg border p-2.5" data-testid={`contact-link-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Select value={l.entity_id} onChange={(e) => updLink(i, { entity_id: e.target.value })} className="flex-1" data-testid={`contact-link-entity-${i}`}>
                    {entities.filter((e) => e.id === l.entity_id || !usedEntities.includes(e.id)).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </Select>
                  <button type="button" onClick={() => rmLink(i)} data-testid={`contact-link-remove-${i}`} className="text-text-secondary hover:text-red-400"><X className="w-4 h-4" /></button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {ROLE_OPTIONS.map((o) => (
                    <button type="button" key={o.value} onClick={() => toggleRole(i, o.value)} data-testid={`contact-link-${i}-role-${o.value}`}
                      className={cn("px-2.5 py-1 rounded-full text-xs border", l.roles.includes(o.value) ? "bg-primary/10 border-primary text-text-primary" : "text-text-secondary")}>
                      {lstr(o.label, lang)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {!f.entity_links.length && <p className="text-xs text-text-secondary">—</p>}
          </div>
        </div>

        <Field label={t("crm_notes")}>
          <textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="contact-notes"
            className="w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:border-[var(--border-strong)] resize-none" />
        </Field>

        {contact.id && (
          <div className="pt-2 border-t">
            <div className="flex items-center gap-1.5 text-xs font-medium text-text-secondary mb-2"><History className="w-3.5 h-3.5" />{t("crm_timeline")}</div>
            <div className="space-y-2 max-h-44 overflow-y-auto" data-testid="contact-timeline">
              {timeline === null ? <Spinner className="w-4 h-4 text-text-secondary" /> :
                timeline.length === 0 ? <p className="text-xs text-text-secondary">{t("crm_no_timeline")}</p> :
                timeline.map((a) => (
                  <div key={a.id} className="flex items-start gap-2 text-xs" data-testid={`timeline-${a.id}`}>
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-sky-400 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-text-primary"><span className="font-medium">{kindLabel(a.kind)}</span>{a.summary ? ` — ${a.summary}` : ""}</p>
                      <p className="text-[10px] text-text-secondary">{a.actor_name} · {new Date(a.created_at).toLocaleDateString(loc)}</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function LogModal({ contact, onClose, onDone }) {
  const { t } = useApp();
  const [kind, setKind] = useState("call");
  const [summary, setSummary] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try { await api.post(`/crm/contacts/${contact.id}/log`, { kind, summary: summary.trim() }); onDone(); }
    finally { setSaving(false); }
  };
  return (
    <Modal open onClose={onClose} title={t("crm_log_title")}
      footer={<><Button variant="outline" onClick={onClose}>{t("cancel")}</Button><Button onClick={submit} disabled={saving} data-testid="log-save">{saving && <Spinner className="w-3.5 h-3.5" />} {t("save")}</Button></>}>
      <div className="space-y-4">
        <p className="text-sm text-text-secondary">{contact.name}</p>
        <Field label={t("crm_interaction_type")}>
          <Select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="log-kind">
            {INT_KINDS.map((k) => <option key={k} value={k}>{t(`crm_int_${k}`)}</option>)}
          </Select>
        </Field>
        <Field label={t("crm_interaction_summary")}>
          <textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="log-summary"
            className="w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:border-[var(--border-strong)] resize-none" />
        </Field>
      </div>
    </Modal>
  );
}

function SettingsModal({ current, onClose, onSaved }) {
  const { t } = useApp();
  const [days, setDays] = useState(current);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try { await api.put("/crm/settings", { relational_threshold_days: Number(days) || 30 }); onSaved(); }
    finally { setSaving(false); }
  };
  return (
    <Modal open onClose={onClose} title={t("crm_settings")}
      footer={<><Button variant="outline" onClick={onClose}>{t("cancel")}</Button><Button onClick={submit} disabled={saving} data-testid="crm-settings-save">{t("save")}</Button></>}>
      <Field label={t("crm_threshold_label")}>
        <Input type="number" min="1" max="365" value={days} onChange={(e) => setDays(e.target.value)} data-testid="crm-threshold-input" />
      </Field>
      <p className="text-xs text-text-secondary mt-2">{t("crm_threshold_hint")}</p>
    </Modal>
  );
}
